import { yupResolver } from '@hookform/resolvers/yup';
import isEqual from 'lodash.isequal';
import { FC, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';

import { createEnterpriseUsersValidation } from '@constants';
import { queryClient } from '@helpers';
import { useConfirmationModal, useModalState } from '@hooks';

import { ApiClientEnterprise } from '@helpers/apiClients';
import { ConfirmModal } from '@organisms/Modals';
import { IInvitationData } from '@organisms/Tables/Enterprise/UsersTable/types';
import { FormData } from './CreateNewEnterpriseUsersForm';
import MainModal from './MainModal';
import SuccessModal from './SuccessModal';
import { errorMessage } from '@services';
import { AxiosError } from 'axios';
import QueryKeys from '@constants/query-keys';

interface CreateNewEnterpriseModalProps {
  onClose: () => void;
  open: boolean;
  id?: string;
}

export const CreateNewEnterpriseUsersModal: FC<CreateNewEnterpriseModalProps> = ({
  onClose,
  open,
  id,
}) => {
  const defaultValues: FormData = useMemo(
    () => ({
      fullName: '',
      role: 'admin',
      email: '',
    }),
    [],
  );

  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { isValid },
  } = useForm<FormData>({
    resolver: yupResolver(createEnterpriseUsersValidation),
    defaultValues,
    mode: 'onChange',
    delayError: 700,
  });

  const values = watch();
  const isDirty = !isEqual(values, defaultValues);

  const handleCloseAndReset = () => {
    reset();
    onClose();
  };

  const [isConfirmOpen, onOpenConfirm, onConfirmCancel, onSubmitConfirm] = useConfirmationModal(
    false,
    handleCloseAndReset,
  );
  const [isSuccessOpen, , openSuccessModal, closeSuccessModal] = useModalState(false);

  const closeAllModal = () => {
    queryClient.refetchQueries(QueryKeys.enterpriseUsers);
    closeSuccessModal();
    reset();
    onClose();
  };

  const getErrorMessage = (errorMessage: string) => {
    switch (errorMessage) {
      case 'INVITATION_EMAIL_EXISTS_ERR':
        return 'Invitation Email Exists!';
      default:
        return 'Add User Error!';
    }
  };

  const handleErrorInvite = (error: AxiosError) => {
    const message = getErrorMessage(error.response?.data?.errorMessage);
    errorMessage(message);
  };

  const { mutate: invite, isLoading } = useMutation(
    (data: IInvitationData) => ApiClientEnterprise.post('/admin/api/v1/invitations', data),
    {
      onSuccess: openSuccessModal,
      onError: handleErrorInvite,
    },
  );

  const onFinalSubmit = (data: FormData) => {
    if (data) {
      const finalData = {
        ...data,
        organizationId: id,
      };
      delete finalData.role;
      invite(finalData as IInvitationData);
    }
  };

  return (
    <>
      <MainModal
        open={open}
        isConfirmOpen={isConfirmOpen}
        isSuccessOpen={isSuccessOpen}
        isDirty={isDirty}
        isValid={isValid}
        isLoading={isLoading}
        control={control}
        handleSubmit={handleSubmit}
        onClose={handleCloseAndReset}
        onOpenConfirm={onOpenConfirm}
        onFinalSubmit={onFinalSubmit}
      />
      <ConfirmModal
        open={isConfirmOpen}
        onCancel={onConfirmCancel}
        onSubmit={onSubmitConfirm}
        title="Are you sure you want to close modal?"
        submitText="Yes, close"
      />
      <SuccessModal open={isSuccessOpen} email={values.email} onClose={closeAllModal} />
    </>
  );
};
